import { getFile } from '../../../services/storage-service';
import { useChatStore } from '../../../state/store/chat/use-chat-store';
import { useEffect, useState } from 'react';
import { useConfig } from 'hooks/use-config';
import { Attachment, FileGenerationStatus, Message, MessageGeneration } from '../types/chat';
import { Download, FileText, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { allSupportedImageExtensions } from '../types/model.types';
import { DownloadAssistantFile } from './DownloadAssistantFile';
import { useTheme } from 'components/core/theme-provider';


interface AssistantImagePreviewProps {
  file: Attachment;
  conversationId?: string;
  message?: Message;
  generation?: MessageGeneration;
  isGeneratedImage?: boolean; // true for generatedImages, false for attachments
  hasParticipants?: boolean; // true for group chat
}

export const AssistantAttachmentPreview = ({
  file,
  hasParticipants,
}: AssistantImagePreviewProps) => {
  const itemIdFilePathMap = useChatStore((state) => state.itemIdFilePathMap);
  const setItemIdFilePathMap = useChatStore((state) => state.setItemIdFilePathMap);
  // const conversations = useChatStore((state) => state.conversations);
  const [isLoading, setIsLoading] = useState(false);
  const [imageSrc, setImageSrc] = useState('');
  const [error, setError] = useState(false);
  const { configurationSettings } = useConfig();
  const theme = useTheme();
  // const hasShownToastRef = useRef(false);

  const isImageFile = allSupportedImageExtensions.includes(`.${file?.extension?.toLowerCase()}`);

  useEffect(() => {
    const fetchImage = async () => {
      if (!file?.fileId || !isImageFile) return;

      const filePath = itemIdFilePathMap.get(file.fileId);
      if (filePath) {
        setImageSrc(filePath);
        return;
      }

      if (file?.status && file?.status === FileGenerationStatus.IN_PROGRESS) {
        return;
      }

      setIsLoading(true);
      setError(false);

      try {
        const response = await getFile(file.fileId, configurationSettings, undefined);
        if (response.isSuccess && response.url) {
          setImageSrc(response.url);
          setItemIdFilePathMap(file.fileId, response.url);
        } else {
          setError(true);
        }
      } catch (error) {
        console.error('Failed to fetch assistant image:', error);
        setError(true);
      }
      setIsLoading(false);
    };

    fetchImage();
  }, [
    file.fileId,
    file.status,
    configurationSettings,
    itemIdFilePathMap,
    setItemIdFilePathMap,
    isImageFile,
  ]);

  // Handle failed file generation - show toast and remove file
  // useEffect(() => {
  //   console.log('file in AssistantAttachmentPreview', file)

  //   if (file.status === FileGenerationStatus.FAILED) {

  //     // Show toast
  //     toast({
  //       variant: 'destructive',
  //       title: t('FILE_GENERATION_FAILED') || 'File Generation Failed',
  //       description: t('FAILED_TO_GENERATE_FILE', { fileName: file.fileName }) || `Failed to generate ${file.fileName}`,
  //     });

  //     // Remove file from the message generation
  //     if (conversationId && message && generation) {
  //       const currentState = useChatStore.getState();
  //       const currentConv = currentState.conversations[conversationId];
  //       if (currentConv) {
  //         const updatedMessages = currentConv.messages.map((msg) => {
  //           if (msg.recordId === message.recordId) {
  //             return {
  //               ...msg,
  //               generations: msg.generations.map((gen) => {
  //                 if (gen.id === generation.id) {
  //                   if (isGeneratedImage) {
  //                     // Remove from generatedImages
  //                     return {
  //                       ...gen,
  //                       generatedImages: gen.generatedImages?.filter(
  //                         (img) => img.fileId !== file.fileId
  //                       ),
  //                     };
  //                   } else {
  //                     // Remove from attachments
  //                     return {
  //                       ...gen,
  //                       attachments: gen.attachments?.filter(
  //                         (att) => att.fileId !== file.fileId
  //                       ),
  //                     };
  //                   }
  //                 }
  //                 return gen;
  //               }),
  //             };
  //           }
  //           return msg;
  //         });

  //         useChatStore.setState({
  //           conversations: {
  //             ...currentState.conversations,
  //             [conversationId]: {
  //               ...currentConv,
  //               messages: updatedMessages,
  //             },
  //           },
  //         });
  //       }
  //     }
  //   }
  // }, [file, conversationId, message, generation, isGeneratedImage]);

  const downloadFile = async () => {
    if (!imageSrc) return;

    const link = document.createElement('a');
    link.href = imageSrc;
    link.download = file.fileName;
    link.click();
  };

  if (!isImageFile) {
    return (
      <div className={`flex items-center justify-between p-2 rounded mt-2 ${theme.theme === 'dark' ? 'bg-[#27272c]' : 'bg-primary-light'}`}>
        <div className="flex items-center gap-2 mr-2">
          <FileText className="h-4 w-4 text-gray-500" />
          <span className={`text-sm ${theme.theme === 'dark' ? 'text-primary-200' : 'text-neutral-800'} truncate max-w-[180px]`}>{file.fileName}</span>
        </div>
        <DownloadAssistantFile file={file} />
      </div>
    );
  }

  if (file.status && file.status === FileGenerationStatus.IN_PROGRESS) {
    return (
      <div className="flex items-center justify-between bg-gray-50 p-2 rounded mt-2">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <span className="text-sm text-gray-500">Generating image...</span>
        </div>
      </div>
    );
  }

  // if (file.status && file.status === FileGenerationStatus.FAILED) {
  //   return null;
  // }


  if (isLoading) {
    return (
      <div className="flex items-center justify-between bg-gray-50 p-2 rounded mt-2">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <span className="text-sm text-gray-500">Loading image...</span>
        </div>
      </div>
    );
  }

  if (error || !imageSrc) {
    return (
      <div className="flex items-center justify-between bg-gray-50 p-2 rounded mt-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-red-500">Failed to load image</span>
        </div>
      </div>
    );
  }

  // console.log('FileGenerationStatus.FAILED', FileGenerationStatus.FAILED, "file.status", file.status)

  return (
    <div className={`flex flex-col gap-2 ${hasParticipants ? '' : 'ml-4'}`}>
      <div className="relative bg-gray-50 rounded overflow-hidden group">
        <img
          className="w-96 h-96 object-cover"
          src={imageSrc}
          alt={file.fileName}
          data-file-id={file.fileId}
        />
        <Button
          size="sm"
          variant="outline"
          className={`absolute bottom-2 right-2 h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white ${theme.theme === 'dark' ? 'hover:bg-white' : 'hover:bg-gray-100'}`}
          onClick={downloadFile}
          title="Download image"
        >
          <Download className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
